/* eslint-disable no-console */
// This is the single place where console output is allowed by design:
// warn/error lines must reach real users' consoles so their screenshots
// are actionable for support.
import { isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import { isSemrushMode } from '@constants/app-modes';
import { isVerboseDebugEnabled } from '@utils/debugMode';

declare const BUILD_VERSION: string;
declare const GIT_SHA: string;
declare const BUILD_TIME: string;

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogEntry = {
  timestamp: string;
  level: LogLevel;
  scope: string;
  message: string;
  data?: unknown;
};

/** How many recent log entries are kept in memory for `window.__respona.dumpLogs()`. */
const LOG_BUFFER_SIZE = 200;

const buffer: LogEntry[] = [];

const getRealmLabel = (): string => {
  if (isSemrushMode()) return 'semrush';
  if (isConciergeRealm()) return 'concierge';
  if (isPublisherRealm()) return 'publisher';
  return 'app';
};

const getVersionLabel = (): string =>
  typeof BUILD_VERSION === 'string' && BUILD_VERSION ? `v${BUILD_VERSION}` : 'v?';

const getPrefix = (): string => `[Respona ${getVersionLabel()} ${getRealmLabel()}]`;

const safeStringify = (data: unknown): string => {
  try {
    return JSON.stringify(data);
  } catch (e) {
    return String(data);
  }
};

const pushToBuffer = (entry: LogEntry): void => {
  buffer.push(entry);
  if (buffer.length > LOG_BUFFER_SIZE) {
    buffer.shift();
  }
};

const shouldPrintVerbose = (): boolean => isVerboseDebugEnabled();

const log = (level: LogLevel, scope: string, message: string, data?: unknown): void => {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    scope,
    message,
    ...(data !== undefined ? { data } : {}),
  };
  pushToBuffer(entry);

  // warn/error always reach the console so end-user screenshots are useful;
  // info/debug only when verbose debugging is enabled (see @utils/debugMode).
  const line = `${getPrefix()} ${entry.timestamp} ${level.toUpperCase()} ${scope} ${message}`;

  if (level === 'error') {
    if (data !== undefined) console.error(line, data);
    else console.error(line);
  } else if (level === 'warn') {
    if (data !== undefined) console.warn(line, data);
    else console.warn(line);
  } else if (shouldPrintVerbose()) {
    if (data !== undefined) console.info(line, data);
    else console.info(line);
  }
};

const dumpLogs = (): string => {
  const text = buffer
    .map(
      (e) =>
        `${e.timestamp} ${e.level.toUpperCase()} ${e.scope} ${e.message}${
          e.data !== undefined ? ` ${safeStringify(e.data)}` : ''
        }`
    )
    .join('\n');
  // Printed so a user can copy it straight from the console when support asks.
  console.log(`${getPrefix()} last ${buffer.length} log entries:\n${text}`);
  return text;
};

const copyLogs = (): Promise<void> => navigator.clipboard.writeText(dumpLogs());

/**
 * Prints a one-time startup banner with build/realm info so any console
 * screenshot immediately tells support which build the user is running.
 * Intentionally bypasses the info-level dev gating.
 */
export const logStartupBanner = (): void => {
  const sha = typeof GIT_SHA === 'string' && GIT_SHA ? GIT_SHA.slice(0, 8) : 'unknown';
  const builtAt = typeof BUILD_TIME === 'string' && BUILD_TIME ? BUILD_TIME : 'unknown';
  const message = `build ${sha}, built ${builtAt} — debug logs: window.__respona.dumpLogs()`;
  pushToBuffer({
    timestamp: new Date().toISOString(),
    level: 'info',
    scope: 'app',
    message,
  });
  console.info(`${getPrefix()} ${message}`);
};

const logger = {
  debug: (scope: string, message: string, data?: unknown): void =>
    log('debug', scope, message, data),
  info: (scope: string, message: string, data?: unknown): void => log('info', scope, message, data),
  warn: (scope: string, message: string, data?: unknown): void => log('warn', scope, message, data),
  error: (scope: string, message: string, data?: unknown): void =>
    log('error', scope, message, data),
  dumpLogs,
  copyLogs,
};

declare global {
  interface Window {
    // eslint-disable-next-line no-underscore-dangle
    __respona?: {
      version: string;
      dumpLogs: () => string;
      copyLogs: () => Promise<void>;
    };
  }
}

if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-underscore-dangle
  window.__respona = {
    version: getVersionLabel(),
    dumpLogs,
    copyLogs,
  };
}

export default logger;
